import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TransactionsType from "enums/TransactionsType";
import { IS_INTEGRATION_CONNECTED } from "../../../graphql/integration";
import { FormikProps } from "formik";
import CustomFieldParentType from "enums/CustomFieldParentType";
import ExpenseCategorySearchAutoComplete from "../../common/CategorySearchAutoComplete";
import AllocationSearchAutoComplete from "components/common/AllocationSearchAutoComplete";
import { FormikTransactionRequirements } from "hooks/useTransactionRequirements";
import { useQuery } from "@apollo/client";
import { Inline, Stack } from "components/layout/reusableLayouts";
import PolicyIcons from "components/common/PolicyIcons";
import TransactionsRequirements from "enums/TransactionsRequirements";
import ViolationStatus from "enums/ViolationStatus";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { ReceiptReviewDetails } from "components/common/receipts/ReceiptReviewDetails";
import { RequirementSections } from "components/common/requirements/RequirementSections";
import type { RequirementTransaction } from "./transactionSectionTypes";

/** The transaction types that carry receipt, category and allocation rules. */
export const REQUIREMENT_TYPES: string[] = [
  TransactionsType.AUTHORIZATION,
  TransactionsType.REFUND,
  TransactionsType.CAPTURE,
  TransactionsType.CASH,
  TransactionsType.MILEAGE,
];

export function customFieldParentTypeFor(
  transactionType?: string,
): CustomFieldParentType | null {
  switch (transactionType) {
    case TransactionsType.CASH:
    case TransactionsType.MILEAGE:
      return CustomFieldParentType.REIMBURSEMENT;
    case TransactionsType.CAPTURE:
    case TransactionsType.AUTHORIZATION:
    case TransactionsType.REFUND:
      return CustomFieldParentType.TRANSACTION;
    default:
      return null;
  }
}

/**
 * Whether the connected integration guessed this transaction's category, which
 * both raises the drawer's warning and decides which section opens first.
 */
export function useIntegrationGuess(
  transaction: RequirementTransaction | undefined,
  setShowIntegrationGuessWarning: (value: boolean) => void,
): boolean {
  const {
    data: isIntegrationConnected,
    refetch: refetchIsIntegrationConnected,
  } = useQuery(IS_INTEGRATION_CONNECTED);
  const [integrationConnected, setIntegrationConnected] = useState<
    boolean | undefined
  >(undefined);

  const hasGuess = Boolean(
    transaction &&
    integrationConnected &&
    transaction.expenseCategory?.id &&
    transaction.expenseCategory?.isSuggestion === true,
  );

  useEffect(() => {
    setShowIntegrationGuessWarning(hasGuess);
  }, [hasGuess, setShowIntegrationGuessWarning]);

  useEffect(() => {
    if (isIntegrationConnected) {
      setIntegrationConnected(isIntegrationConnected.isIntegrationConnected);
    }
  }, [isIntegrationConnected]);

  useEffect(() => {
    refetchIsIntegrationConnected();
  }, [refetchIsIntegrationConnected]);

  return hasGuess;
}

interface ExpenseCategoryPanelProps {
  formik: FormikProps<FormikTransactionRequirements>;
}

const ExpenseCategoryPanel: React.FC<ExpenseCategoryPanelProps> = ({
  formik,
}) => {
  return (
    <Inline gap="var(--space-7)" align="start">
      <ExpenseCategorySearchAutoComplete
        expenseCategory={formik.values.expenseCategory}
        onSelectExpenseCategory={(expenseCategory) => {
          formik.setFieldValue("expenseCategory", expenseCategory);
        }}
        hideClearButton={true}
      />
    </Inline>
  );
};

interface AllocationsPanelProps {
  formik: FormikProps<FormikTransactionRequirements>;
}

const AllocationsPanel: React.FC<AllocationsPanelProps> = ({ formik }) => {
  return (
    <Stack gap="var(--space-7)">
      <AllocationSearchAutoComplete
        title={null}
        allocation={formik.values.allocationCode}
        allocationType={formik.values.allocationType}
        onSelectAllocation={(allocation) => {
          formik.setFieldValue("allocationCode", {
            id: allocation?.id,
            allocationCode: allocation?.allocationCode,
          });
          formik.setFieldValue("allocationType", allocation?.allocationType);
        }}
      />
    </Stack>
  );
};

export function receiptNeedsAction(
  transaction: RequirementTransaction | undefined,
  canAdjudicateMismatch: boolean,
): boolean {
  const receipt = transaction?.policyStatus?.receipt;
  if (!receipt || receipt.isExempt) return false;
  switch (receipt.violationStatus) {
    case ViolationStatus.MISSING:
      return true;
    case ViolationStatus.LOST:
      return Boolean(transaction?.isLostApprovalAllowed);
    case ViolationStatus.MISMATCH:
      return canAdjudicateMismatch;
    default:
      return false;
  }
}

export function allocationNeedsAction(
  transaction: RequirementTransaction | undefined,
): boolean {
  const allocation = transaction?.policyStatus?.allocation;
  return Boolean(
    allocation &&
    !allocation.isExempt &&
    allocation.violationStatus === ViolationStatus.MISSING,
  );
}

interface RequirementsPanelProps {
  transaction: RequirementTransaction;
  formik: FormikProps<FormikTransactionRequirements>;
  refetchTransactionData: () => void;
  handleRefreshTransactions?: () => void;
  hasIntegrationGuess: boolean;
}

/**
 * Receipts, category and allocation as one panel. They share a tab because
 * they are one decision: whether this transaction is compliant. The glyphs
 * stay on the section headings so all three statuses read while collapsed.
 */
export const RequirementsPanel: React.FC<RequirementsPanelProps> = ({
  transaction,
  formik,
  refetchTransactionData,
  handleRefreshTransactions,
  hasIntegrationGuess,
}) => {
  const { t } = useTranslation();
  const canAdjudicateMismatch = useCheckUserRole(Role.Admin);

  return (
    <RequirementSections
      recordId={transaction?.id}
      sections={[
        {
          key: "receipts",
          title: t("Receipts"),
          icon: (
            <PolicyIcons
              violationStatus={
                transaction?.policyStatus?.receipt?.violationStatus ?? undefined
              }
              violationType="receipt"
              isExempt={
                transaction?.policyStatus?.receipt?.isExempt ?? undefined
              }
            />
          ),
          needsAction: receiptNeedsAction(transaction, canAdjudicateMismatch),
          content: (
            <ReceiptReviewDetails
              transaction={transaction}
              refetchTransactionData={refetchTransactionData}
              handleRefreshTransactions={handleRefreshTransactions}
            />
          ),
        },
        {
          key: "categories",
          title: t("Expense Categories"),
          icon: (
            <PolicyIcons
              violationStatus={TransactionsRequirements.ViewCategories}
              violationType="category"
            />
          ),
          needsAction: hasIntegrationGuess,
          content: <ExpenseCategoryPanel formik={formik} />,
        },
        {
          key: "allocations",
          title: t("Allocations"),
          icon: (
            <PolicyIcons
              violationStatus={
                transaction?.policyStatus?.allocation?.violationStatus ??
                undefined
              }
              violationType="allocation"
              isExempt={
                transaction?.policyStatus?.allocation?.isExempt ?? undefined
              }
            />
          ),
          needsAction: allocationNeedsAction(transaction),
          content: <AllocationsPanel formik={formik} />,
        },
      ]}
    />
  );
};
